/**
 * serviceWorkerRegistration — wires the service worker placed at
 * `public/service-worker.js` into the page lifecycle.
 *
 * - In production builds (NODE_ENV === 'production'), the SW is registered.
 * - In development, it's unregistered (HMR + SW disagree about cache freshness).
 * - The browser auto-checks for SW updates on each page load; new versions
 *   activate on the next full reload.
 */

const isLocalhost = Boolean(
  typeof window !== 'undefined' && window.location && (
    window.location.hostname === 'localhost' ||
    window.location.hostname === '[::1]' ||
    /^127(?:\.\d{1,3}){3}$/.test(window.location.hostname)
  )
);

export function register(): void {
  if (process.env.NODE_ENV !== 'production') return;
  if (!('serviceWorker' in navigator)) return;
  // Skip registration on file:// or other non-http schemes
  if (!/^https?:/.test(window.location.protocol)) return;

  window.addEventListener('load', () => {
    const swUrl = `${process.env.PUBLIC_URL || ''}/service-worker.js`;
    if (isLocalhost) {
      // Validate that the SW URL responds before registering, otherwise
      // a stale shell can be served forever on a dev machine.
      checkValidServiceWorker(swUrl);
    } else {
      registerValidSW(swUrl);
    }
  });
}

/**
 * Dispatch the in-app "Update available" toast (rendered by
 * `<PwaUpdatePrompt>` mounted from App.tsx). Carries the waiting SW so
 * the prompt can postMessage SKIP_WAITING when the user clicks Refresh.
 */
function announceUpdate(worker: ServiceWorker | null) {
  try {
    window.dispatchEvent(new CustomEvent('pwa:update-available', { detail: worker }));
  } catch { /* ignore */ }
}

function registerValidSW(swUrl: string): void {
  navigator.serviceWorker.register(swUrl).then((reg) => {
    // If a SW is ALREADY waiting (user opened the app while a previous
    // session had installed a newer SW), prompt immediately.
    if (reg.waiting && navigator.serviceWorker.controller) {
      announceUpdate(reg.waiting);
    }
    // Detect a NEW SW becoming installed (i.e. update available) and notify.
    reg.onupdatefound = () => {
      const installing = reg.installing;
      if (!installing) return;
      installing.onstatechange = () => {
        if (installing.state === 'installed' && navigator.serviceWorker.controller) {
          // New SW is installed but the current page is still controlled
          // by the OLD one. Surface a refresh prompt to the user.
          // eslint-disable-next-line no-console
          console.info('[SW] Update available — refresh to apply.');
          announceUpdate(reg.waiting || installing);
        }
      };
    };
    // Also re-check for updates each time the user brings the PWA window
    // back into focus (covers the "PWA stayed open for days" case).
    const recheck = () => { try { reg.update(); } catch { /* ignore */ } };
    window.addEventListener('focus', recheck);
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') recheck();
    });
  }).catch((err) => {
    // eslint-disable-next-line no-console
    console.warn('[SW] Registration failed:', err);
  });
}

function checkValidServiceWorker(swUrl: string): void {
  fetch(swUrl, { headers: { 'Service-Worker': 'script' } }).then((response) => {
    const ct = response.headers.get('content-type') || '';
    if (response.status === 404 || (!ct.includes('javascript') && !ct.includes('text/plain'))) {
      navigator.serviceWorker.ready.then((reg) => reg.unregister()).then(() => {
        window.location.reload();
      });
    } else {
      registerValidSW(swUrl);
    }
  }).catch(() => {
    // eslint-disable-next-line no-console
    console.info('[SW] No internet connection — app running from cache.');
  });
}

export function unregister(): void {
  if (!('serviceWorker' in navigator)) return;
  navigator.serviceWorker.ready.then((reg) => reg.unregister()).catch(() => { /* ignore */ });
}
